/**
 * Offboarding page — checklist templates (Admin) + assignments with progress.
 */
import { useMemo, useState } from 'react';
import {
  ClipboardX,
  Plus,
  Check,
  X,
  ListChecks,
} from 'lucide-react';
import {
  useOffboardingAssignments,
  useOffboardingTemplates,
  useAssignOffboarding,
  useCreateOffboardingTemplate,
  useCompleteOffboardingItem,
  useCancelOffboardingAssignment,
  useOffboardingAssignment,
} from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Field, Textarea } from '../../../shell/Field.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import type { OffboardingAssignment, OffboardingStatus } from '@shared/types';

const statusTone: Record<OffboardingStatus, 'blue' | 'green' | 'slate'> = {
  InProgress: 'blue',
  Completed: 'green',
  Cancelled: 'slate',
};

const DEFAULT_TEMPLATE_ITEMS = [
  'Return equipment',
  'Return Key Fob',
  'Revoke accounts',
  'Exit interview',
  'Final paycheck/PTO payout review',
];

export function OffboardingPage() {
  const { user } = useAuthStore();
  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const isAdmin = user?.role === 'Admin';
  const { data, isLoading } = useOffboardingAssignments();
  const { data: directory } = useEmployeeDirectory();
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const cancel = useCancelOffboardingAssignment();

  const columns: Column<OffboardingAssignment>[] = [
    {
      key: 'employee',
      header: 'Employee',
      render: (a) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {employeeLabel.get(a.employeeId) ?? (a.employeeId === user?.employeeId ? 'You' : 'Employee')}
        </span>
      ),
    },
    {
      key: 'template',
      header: 'Checklist',
      render: (a) => <span className="font-medium">{a.templateName}</span>,
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (a) => {
        const done = a.completedCount ?? 0;
        const total = a.totalCount ?? 0;
        return (
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {done}/{total}
            {(a.requiredRemaining ?? 0) > 0 ? (
              <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                ({a.requiredRemaining} required left)
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <Badge tone={statusTone[a.status]}>{a.status}</Badge>,
    },
    {
      key: 'due',
      header: 'Due',
      render: (a) => (a.dueDate ? formatDate(a.dueDate) : '—'),
    },
    {
      key: 'actions',
      header: '',
      render: (a) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setDetailId(a.id);
            }}
          >
            Open
          </Button>
          {canManage && a.status === 'InProgress' && (
            <Button
              size="sm"
              variant="ghost"
              aria-label="Cancel checklist"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await cancel.mutateAsync(a.id);
                  toast('success', 'Checklist cancelled.');
                } catch (err) {
                  const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to cancel.';
                  toast('error', msg);
                }
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Offboarding</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data?.items.length ?? 0} checklist{(data?.items.length ?? 0) === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="secondary" onClick={() => setTemplateOpen(true)}>
              <ListChecks className="h-4 w-4" /> New template
            </Button>
          )}
          {canManage && (
            <Button onClick={() => setAssignOpen(true)}>
              <Plus className="h-4 w-4" /> Assign checklist
            </Button>
          )}
        </div>
      </div>

      {isAdmin && <TemplateSummary />}

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState
          icon={<ClipboardX className="h-8 w-8" />}
          title="No offboarding checklists"
          description={
            canManage
              ? 'Create a template, then assign it when someone is leaving.'
              : 'When HR assigns a checklist, it will show up here.'
          }
        />
      )}

      {canManage && (
        <AssignModal open={assignOpen} onClose={() => setAssignOpen(false)} />
      )}
      {isAdmin && (
        <TemplateModal open={templateOpen} onClose={() => setTemplateOpen(false)} />
      )}
      {detailId && (
        <AssignmentDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

function TemplateSummary() {
  const { data } = useOffboardingTemplates();
  const templates = data?.items ?? [];
  if (templates.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Templates</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {templates.map((t) => (
          <Badge key={t.id} tone={t.isActive ? 'green' : 'slate'}>
            {t.name}{t.isActive ? '' : ' (inactive)'}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function AssignModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const assign = useAssignOffboarding();
  const { data: templates } = useOffboardingTemplates({ activeOnly: true });
  const [employeeId, setEmployeeId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [dueDate, setDueDate] = useState('');

  const handleClose = () => {
    setEmployeeId('');
    setTemplateId('');
    setDueDate('');
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await assign.mutateAsync({
        employeeId,
        templateId,
        dueDate: dueDate ? new Date(`${dueDate}T00:00:00.000Z`).toISOString() : null,
      });
      toast('success', 'Checklist assigned.');
      handleClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to assign checklist.';
      toast('error', msg);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Assign offboarding checklist">
      <div className="space-y-4">
        <EmployeeSelect
          label="Employee"
          value={employeeId}
          onChange={setEmployeeId}
          directory
        />
        <Select
          label="Template"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          <option value="">Select a template…</option>
          {(templates?.items ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
        <Field label="Due date (optional)">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={assign.isPending}
            disabled={!employeeId || !templateId}
          >
            Assign
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplateModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateOffboardingTemplate();
  const [name, setName] = useState('Employee Offboarding');
  const [description, setDescription] = useState('');
  const [itemsText, setItemsText] = useState(DEFAULT_TEMPLATE_ITEMS.join('\n'));

  const handleClose = () => {
    setName('Employee Offboarding');
    setDescription('');
    setItemsText(DEFAULT_TEMPLATE_ITEMS.join('\n'));
    onClose();
  };

  const handleSubmit = async () => {
    const titles = itemsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (titles.length === 0) {
      toast('error', 'Add at least one checklist item.');
      return;
    }
    try {
      await create.mutateAsync({
        name,
        description,
        isActive: true,
        items: titles.map((title, index) => ({
          title,
          description: '',
          sortOrder: index,
          required: true,
        })),
      });
      toast('success', 'Template created.');
      handleClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to create template.';
      toast('error', msg);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="New offboarding template">
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </Field>
        <Field label="Checklist items (one per line)">
          <Textarea value={itemsText} onChange={(e) => setItemsText(e.target.value)} rows={8} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={create.isPending} disabled={!name.trim()}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AssignmentDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: assignment, isLoading } = useOffboardingAssignment(id);
  const complete = useCompleteOffboardingItem();
  const { user } = useAuthStore();

  const canToggle = (a: OffboardingAssignment) => {
    if (a.status === 'Cancelled') return false;
    if (user?.role === 'Admin' || user?.role === 'Manager') return true;
    return a.employeeId === user?.employeeId;
  };

  const toggle = async (itemId: string, undo: boolean) => {
    try {
      await complete.mutateAsync({ assignmentId: id, itemId, undo });
      toast('success', undo ? 'Item reopened.' : 'Item completed.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update item.';
      toast('error', msg);
    }
  };

  return (
    <Modal open onClose={onClose} title={assignment?.templateName ?? 'Checklist'}>
      {isLoading || !assignment ? (
        <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <Badge tone={statusTone[assignment.status]}>{assignment.status}</Badge>
            <span>
              {assignment.completedCount ?? 0}/{assignment.totalCount ?? 0} done
            </span>
            {assignment.dueDate && <span>Due {formatDate(assignment.dueDate)}</span>}
          </div>
          <ul className="space-y-2">
            {(assignment.items ?? []).map((item) => {
              const done = !!item.completedAt;
              return (
                <li
                  key={item.id}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
                >
                  <button
                    type="button"
                    disabled={!canToggle(assignment) || complete.isPending}
                    onClick={() => toggle(item.id, done)}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      done
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-slate-300 dark:border-slate-600'
                    } disabled:opacity-50`}
                    aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                  >
                    {done && <Check className="h-3.5 w-3.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
                      {item.title}
                      {item.required && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-600">required</span>
                      )}
                    </p>
                    {item.description && (
                      <p className="mt-0.5 text-xs text-slate-500">{item.description}</p>
                    )}
                    {done && item.completedAt && (
                      <p className="mt-1 text-[11px] text-slate-400">Done {formatDate(item.completedAt)}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-end pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
